import { useState } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { X, Send, Eye, AlertTriangle } from "lucide-react";
import DropZone from "@/components/shared/DropZone";
import { FISCAL_YEARS } from "@/constants/modules";
import { COMM_TYPES, ACCEPTED_EXTS, MAX_ATTACHMENT_MB } from "./commHelpers";
import EmailPreviewModal from "./EmailPreviewModal";
import SmsOptIn from "@/components/admin/SmsOptIn";

export default function SendForm({ clients, onSend, onCancel }) {
  const [clientId, setClientId] = useState("");
  const [commType, setCommType] = useState("document");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [taxYear, setTaxYear] = useState(FISCAL_YEARS[0]);
  const [loading, setLoading] = useState(false);
  const [searchClient, setSearchClient] = useState("");
  const [errors, setErrors] = useState({});
  // Batch #14.336 — texto (lien portail, texte validé)
  const [smsOpt, setSmsOpt] = useState({ send_sms: false, sms_text: "", sms_to: "" });
  // Iter107 — Mode email
  const [emailMode, setEmailMode] = useState("generic"); // 'generic' (defaut) | 'custom'
  const [emailCustomText, setEmailCustomText] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const filtered = clients.filter(c => {
    const name = `${c.first_name} ${c.last_name}`.toLowerCase();
    return name.includes(searchClient.toLowerCase());
  });

  const validate = () => {
    const errs = {};
    if (!clientId) errs.clientId = "Selectionnez un client";
    if (!title.trim()) errs.title = "Titre requis";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error(Object.values(errs).join(" — "));
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("client_id", clientId);
      fd.append("direction", "sent");
      fd.append("comm_type", commType);
      fd.append("title", title.trim());
      fd.append("message", message.trim());
      fd.append("tax_year", taxYear);
      // Iter107 — Mode email
      fd.append("email_mode", emailMode);
      if (emailMode === "custom") {
        fd.append("email_custom_text", emailCustomText.trim());
      }
      files.forEach(f => fd.append("files", f));
      if (smsOpt.send_sms) {
        fd.append("notify_sms", "true");
        fd.append("sms_text", smsOpt.sms_text || "");
        fd.append("sms_to", smsOpt.sms_to || "");
      }
      const r = await api.post("/communications", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Communication envoyee");
      if (smsOpt.send_sms) {
        if (r.data.sms_sent) toast.success(`Texto envoyé à ${r.data.sms_to}`);
        else toast.error(`Texto non envoyé : ${r.data.sms_error || "erreur"}`);
      }
      onSend();
    } catch (err) {
      toast.error("Erreur: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Iter107 — Apercu email avant envoi
  const handlePreview = async () => {
    if (!clientId) {
      toast.error("Selectionnez un client pour generer l'apercu");
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await api.post("/communications/email-preview", {
        client_id: clientId,
        comm_type: commType,
        email_mode: emailMode,
        email_custom_text: emailMode === "custom" ? emailCustomText.trim() : "",
      });
      setPreview(res.data);
    } catch (err) {
      toast.error("Erreur apercu: " + (err.response?.data?.detail || err.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="send-form">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Send className="w-4 h-4 text-blue-600" /> Nouvel envoi
        </h3>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Client */}
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Client *</label>
          <input type="text" placeholder="Rechercher un client..." value={searchClient}
            onChange={e => setSearchClient(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm mb-1" data-testid="client-search" />
          {(searchClient || !clientId) && (
            <div className="max-h-32 overflow-y-auto border rounded-lg">
              {filtered.slice(0, 10).map(c => (
                <button key={c.id} type="button"
                  onClick={() => { setClientId(c.id); setSearchClient(`${c.first_name} ${c.last_name}`); setErrors({ ...errors, clientId: null }); }}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 ${clientId === c.id ? "bg-blue-50 font-medium" : ""}`}
                  data-testid={`client-opt-${c.id}`}>
                  {c.first_name} {c.last_name}
                </button>
              ))}
            </div>
          )}
          {errors.clientId && <p className="text-xs text-red-500 mt-0.5">{errors.clientId}</p>}
        </div>

        {/* Type */}
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Type d'action *</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {COMM_TYPES.map(ct => (
              <button key={ct.id} type="button" onClick={() => setCommType(ct.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition ${commType === ct.id ? "border-blue-500 bg-blue-50 text-blue-700" : "hover:bg-gray-50"}`}
                data-testid={`type-${ct.id}`}>
                <ct.icon className="w-4 h-4" /> {ct.label}
              </button>
            ))}
          </div>
        </div>

        {/* Title + year */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-gray-500 mb-1 block">Titre *</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm ${errors.title ? "border-red-300 bg-red-50" : ""}`}
              placeholder="Ex: Facture janvier 2026" maxLength={200} data-testid="comm-title" />
            {errors.title && <p className="text-xs text-red-500 mt-0.5">{errors.title}</p>}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Annee fiscale</label>
            <select value={taxYear} onChange={e => setTaxYear(Number(e.target.value))}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="comm-year">
              {FISCAL_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        {/* Message */}
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Message</label>
          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4}
            className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
            placeholder="Description ou instructions..." maxLength={5000} data-testid="comm-message" />
        </div>

        {/* Files — drag & drop */}
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Pieces jointes</label>
          <DropZone
            onFilesAdded={(added) => setFiles([...files, ...added])}
            files={files}
            onRemove={(i) => setFiles(files.filter((_, j) => j !== i))}
            accept={ACCEPTED_EXTS}
            maxSize={MAX_ATTACHMENT_MB * 1024 * 1024}
            compact
            label="Glissez vos fichiers ou cliquez"
            testIdPrefix="comm-files"
          />
        </div>

        {/* Iter107 — Mode email + apercu */}
        <div className="border rounded-lg p-3 space-y-3 bg-slate-50" data-testid="email-mode-section">
          <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">Email envoye au client</div>
          <div className="flex gap-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="email_mode"
                value="generic"
                checked={emailMode === "generic"}
                onChange={() => setEmailMode("generic")}
                data-testid="email-mode-generic"
              />
              <span>Mode generique <span className="text-emerald-700 text-xs">(defaut securise)</span></span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="email_mode"
                value="custom"
                checked={emailMode === "custom"}
                onChange={() => setEmailMode("custom")}
                data-testid="email-mode-custom"
              />
              <span>Mode personnalise</span>
            </label>
          </div>

          {emailMode === "custom" && (
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="email-custom-warning">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Attention : ce message sera envoye tel quel au client.</span>
              </div>

              <textarea
                value={emailCustomText}
                onChange={(e) => setEmailCustomText(e.target.value)}
                rows={4}
                maxLength={5000}
                placeholder="Message personnalise envoye au client..."
                className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
                data-testid="email-custom-text"
              />
            </div>
          )}

          <button
            type="button"
            onClick={handlePreview}
            disabled={previewLoading || !clientId}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-white text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="email-preview-btn"
          >
            <Eye className="w-4 h-4" /> {previewLoading ? "Chargement..." : "Apercu email"}
          </button>
        </div>

        {/* Batch #14.336 — Texto : lien du portail seulement (jamais la pièce), texte validé avant l'envoi */}
        <div className="border rounded-lg p-3 space-y-2 bg-orange-50/40" data-testid="sms-mode-section">
          <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">Texto au client</div>
          <SmsOptIn previewBody={{ kind: "comm", client_id: clientId, title: title.trim(), comm_type: commType }} checked={smsOpt.send_sms} onChange={setSmsOpt}
            label="Prévenir aussi par texto (le client reçoit le lien de son portail, pas la pièce jointe)" testId="comm-sms-optin" accent="accent-orange-600" />
        </div>

        <div className="flex gap-2 pt-2">
          <button type="submit" disabled={loading}
            className="flex items-center gap-2 bg-[#072354] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[#0a2d6e] transition disabled:opacity-50"
            data-testid="send-btn">
            <Send className="w-4 h-4" /> {loading ? "Envoi..." : "Envoyer"}
          </button>
          <button type="button" onClick={onCancel}
            className="px-4 py-2.5 rounded-lg text-sm border hover:bg-gray-50">
            Annuler
          </button>
        </div>
      </form>

      {/* Iter107 — Modal apercu email */}
      <EmailPreviewModal preview={preview} mode={emailMode} onClose={() => setPreview(null)} />
    </div>
  );
}
